import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import { fi } from './assets/i18n/fi'
import { en } from './assets/i18n/en'
import { sv } from './assets/i18n/sv'
import { Lang, Translations } from './assets/i18n'

import * as O from 'fp-ts/lib/Option'
import { pipe } from 'fp-ts/lib/pipeable'
import { uiRootUrl } from './session'

const defaultLocale: Lang = 'fi'
const availableLocales: Lang[] = ['fi', 'sv', 'en']

i18n.use(initReactI18next).init({
  defaultNS: 'translations',
  fallbackLng: availableLocales,
  interpolation: {
    escapeValue: false // not needed for react!!
  },
  lng: defaultLocale,
  ns: ['translations'],
  react: {
    wait: true
  },
  resources: {
    fi: {
      translations: fi
    },
    sv: {
      translations: sv
    },
    en: {
      translations: en
    }
  }
})

export function t<P1 extends keyof NonNullable<Translations>>(
  p1: P1,
  options?: i18n.TOptions
): NonNullable<Translations>[P1]
export function t<
  P1 extends keyof NonNullable<Translations>,
  P2 extends keyof NonNullable<NonNullable<Translations>[P1]>
>(
  p: [P1, P2],
  options?: i18n.TOptions
): NonNullable<NonNullable<Translations>[P1]>[P2]
export function t<
  P1 extends keyof NonNullable<Translations>,
  P2 extends keyof NonNullable<NonNullable<Translations>[P1]>,
  P3 extends keyof NonNullable<NonNullable<NonNullable<Translations>[P1]>[P2]>
>(
  p: [P1, P2, P3],
  options?: i18n.TOptions
): NonNullable<NonNullable<NonNullable<Translations>[P1]>[P2]>[P3]
export function t<
  P1 extends keyof NonNullable<Translations>,
  P2 extends keyof NonNullable<NonNullable<Translations>[P1]>,
  P3 extends keyof NonNullable<NonNullable<NonNullable<Translations>[P1]>[P2]>,
  P4 extends keyof NonNullable<
    NonNullable<NonNullable<NonNullable<Translations>[P1]>[P2]>[P3]
  >
>(
  p: [P1, P2, P3, P4],
  options?: i18n.TOptions
): NonNullable<
  NonNullable<NonNullable<NonNullable<Translations>[P1]>[P2]>[P3]
>[P4]
export function t<
  P1 extends keyof NonNullable<Translations>,
  P2 extends keyof NonNullable<NonNullable<Translations>[P1]>,
  P3 extends keyof NonNullable<NonNullable<NonNullable<Translations>[P1]>[P2]>,
  P4 extends keyof NonNullable<
    NonNullable<NonNullable<NonNullable<Translations>[P1]>[P2]>[P3]
  >,
  P5 extends keyof NonNullable<
    NonNullable<
      NonNullable<NonNullable<NonNullable<Translations>[P1]>[P2]>[P3]
    >[P4]
  >
>(
  p: [P1, P2, P3, P4, P5],
  options?: i18n.TOptions
): NonNullable<
  NonNullable<
    NonNullable<NonNullable<NonNullable<Translations>[P1]>[P2]>[P3]
  >[P4]
>[P5]
export function t<
  P1 extends keyof NonNullable<Translations>,
  P2 extends keyof NonNullable<NonNullable<Translations>[P1]>,
  P3 extends keyof NonNullable<NonNullable<NonNullable<Translations>[P1]>[P2]>,
  P4 extends keyof NonNullable<
    NonNullable<NonNullable<NonNullable<Translations>[P1]>[P2]>[P3]
  >,
  P5 extends keyof NonNullable<
    NonNullable<
      NonNullable<NonNullable<NonNullable<Translations>[P1]>[P2]>[P3]
    >[P4]
  >,
  P6 extends keyof NonNullable<
    NonNullable<
      NonNullable<
        NonNullable<NonNullable<NonNullable<Translations>[P1]>[P2]>[P3]
      >[P4]
    >[P5]
  >
>(
  p: [P1, P2, P3, P4, P5, P6],
  options?: i18n.TOptions
): NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<NonNullable<NonNullable<Translations>[P1]>[P2]>[P3]
    >[P4]
  >[P5]
>[P6]
export function t(p: string[], options?: i18n.TOptions): any {
  return i18n.t(p.join('.'), options)
}

function getLang(lang: string): Lang {
  return pipe(
    lang.match(
      new RegExp(
        availableLocales.map((x: Lang) => `${uiRootUrl}\/${x}`).join('|')
      )
    ),
    O.fromNullable,
    O.map((s) => s[0].replace(uiRootUrl, '').replace(new RegExp('/', 'g'), '')),
    O.map(toLang),
    O.getOrElse(() => defaultLocale)
  )
}

function isLang(x: string | Lang): x is Lang {
  return availableLocales.includes(x as Lang)
}

function toLang(lang: string | Lang): Lang {
  return lang && isLang(lang) ? lang : defaultLocale
}

const currentLang: Lang = getLang(window.location.pathname)

export { defaultLocale, availableLocales, i18n, currentLang }
